import {Injectable} from '@angular/core';
import {BehaviorSubject, combineLatest, Observable, Subscription, switchMap, timer} from 'rxjs';
import {filter, map, take} from 'rxjs/operators';
import {
	BeneImmobileDossierModel,
	BeneImmobileModel,
	JobSalvataggioImmobiliModel,
	StatoJobCatasto
} from '../../../shared/models';
import {PatrimonioImmobiliareService} from './patrimonio-immobiliare.service';
import {DataStateService} from '../state/data-state.service';

/**
 * Segue il salvataggio massivo di immobili eseguito in background dal backend. Vive a livello di applicazione
 * perché l'indicatore di avanzamento sta nel layout principale.
 */
@Injectable({providedIn: 'root'})
export class SalvataggioImmobiliJobService {

	private static readonly INTERVALLO_POLLING_MS = 3000;

	private readonly jobSubject = new BehaviorSubject<JobSalvataggioImmobiliModel | null>(null);
	readonly job$: Observable<JobSalvataggioImmobiliModel | null> = this.jobSubject.asObservable();

	// dossier a cui appartiene il salvataggio: fuori da quello l'indicatore non ha senso
	private rootIdJob: string = null;

	get inCorso(): boolean {
		return this.jobSubject.value?.stato === StatoJobCatasto.IN_CORSO;
	}

	private pollingSub: Subscription;

	constructor(private immobiliareService: PatrimonioImmobiliareService,
	            private state: DataStateService) {
	}

	/**
	 * Il job filtrato sul dossier aperto: emette null quando l'utente esce dal dossier in cui il salvataggio è
	 * partito, e torna a emetterlo quando ci rientra.
	 */
	readonly jobDossierCorrente$: Observable<JobSalvataggioImmobiliModel | null> = combineLatest([
		this.job$,
		this.state.idRoot$
	]).pipe(map(([job, idRootCorrente]) => (job && this.rootIdJob && this.rootIdJob === idRootCorrente) ? job : null));

	/**
	 * Avvia il salvataggio sul server e ne segue lo stato, emettendo gli immobili salvati quando è concluso.
	 * L'observable restituito si comporta come la vecchia chiamata sincrona, così il chiamante non cambia.
	 */
	avvia(immobili: BeneImmobileModel[], rootId?: string, soggettoId?: string): Observable<BeneImmobileDossierModel[]> {
		this.annullaPolling();
		this.jobSubject.next(null);
		this.rootIdJob = this.state.getIdRoot();

		// il monitoraggio sta qui e non nella catena restituita, altrimenti si fermerebbe se il chiamante muore
		this.immobiliareService.avviaSalvataggioImmobili(immobili, rootId, soggettoId).subscribe({
			next: job => {
				this.jobSubject.next(job);
				this.avviaPolling(job.jobId);
			},
			error: errore => this.jobSubject.next(this.jobFallito(immobili.length, errore?.message))
		});

		return this.attendiRisultato();
	}

	/**
	 * Emette gli immobili salvati appena il lavoro si conclude, o solleva l'errore se è fallito.
	 */
	private attendiRisultato(): Observable<BeneImmobileDossierModel[]> {
		return this.job$.pipe(
			filter(job => !!job && job.stato !== StatoJobCatasto.IN_CORSO),
			take(1),
			map(job => {
				if (job.stato === StatoJobCatasto.ERRORE) {
					throw new Error(job.errore);
				}
				return job.risultato;
			})
		);
	}

	private avviaPolling(jobId: string): void {
		this.pollingSub = timer(SalvataggioImmobiliJobService.INTERVALLO_POLLING_MS, SalvataggioImmobiliJobService.INTERVALLO_POLLING_MS)
			.pipe(switchMap(() => this.immobiliareService.getStatoSalvataggioImmobili(jobId)))
			.subscribe({
				next: stato => {
					this.jobSubject.next(stato);
					if (stato.stato !== StatoJobCatasto.IN_CORSO) {
						this.annullaPolling();
					}
				},
				// un errore di rete non deve interrompere il monitoraggio: il salvataggio prosegue lato server
				error: () => this.avviaPolling(jobId)
			});
	}

	private jobFallito(totaleImmobili: number, errore: string): JobSalvataggioImmobiliModel {
		return {
			jobId: null,
			stato: StatoJobCatasto.ERRORE,
			totaleImmobili: totaleImmobili,
			errore: errore,
			inizioMs: 0,
			fineMs: 0
		};
	}

	/**
	 * Da chiamare quando l'esito è stato applicato alla pagina: senza questo l'indicatore resterebbe a video.
	 */
	pulisci(): void {
		this.annullaPolling();
		this.rootIdJob = null;
		this.jobSubject.next(null);
	}

	private annullaPolling(): void {
		this.pollingSub?.unsubscribe();
		this.pollingSub = null;
	}
}
